import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTimes, FaUserCheck, FaUsers } from 'react-icons/fa';

const AssignTicketModal = ({ isOpen, onClose, ticket, userInfo, API_BASE_URL, auth, onSuccess }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);

  useEffect(() => {
    if (isOpen && ticket) {
      fetchUsers();
    }
  }, [isOpen, ticket]);

  const fetchUsers = async () => {
    try {
      setFetchingUsers(true);
      // Get users from the same MMDA who can handle tickets
      const response = await axios.get(
        `${API_BASE_URL}/users?mmda_id=${userInfo.mmda_id}`,
        auth
      );
      
      // Filter to only include MMDA admins and other relevant roles
      const eligibleUsers = response.data.filter(user => 
        user.user_id !== ticket.created_by && // Don't assign back to creator
        user.status === 'active' &&
        (user.role_name === 'mmda_admin' || user.role_name === 'super_admin')
      );
      
      setUsers(eligibleUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setFetchingUsers(false);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    
    if (!selectedUser) {
      alert('Please select a user to assign the ticket to');
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/support-tickets/${ticket.ticket_id}/assign`,
        {
          assigned_to: selectedUser,
          assigned_by: userInfo.user_id
        },
        auth
      );

      onSuccess();
    } catch (error) {
      console.error('Error assigning ticket:', error);
      alert('Failed to assign ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !ticket) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        padding: '2rem',
        width: '90%',
        maxWidth: '500px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <h2 style={{ margin: 0, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FaUserCheck color="#f39c12" />
            Assign Ticket
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#7f8c8d'
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Ticket Info */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1rem',
          borderRadius: '0.25rem',
          marginBottom: '2rem'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0' }}>Ticket #{ticket.ticket_number}</h4>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>{ticket.title}</p>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#7f8c8d' }}>
            Created by {ticket.creator_name}
          </p>
        </div>

        {/* Assignment Form */}
        <form onSubmit={handleAssign}>
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              <FaUsers style={{ marginRight: '0.5rem' }} />
              Assign to:
            </label>
            
            {fetchingUsers ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#7f8c8d' }}>
                Loading users...
              </div>
            ) : users.length === 0 ? (
              <div style={{
                padding: '1rem',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '0.25rem',
                color: '#856404'
              }}>
                No eligible users found for assignment. Only MMDA admins can be assigned tickets.
              </div>
            ) : (
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
                required
              >
                <option value="">Select a user...</option>
                {users.map(user => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.full_name} ({user.role_name}) - {user.email}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Info Note */}
          <div style={{
            backgroundColor: '#e8f4fd',
            border: '1px solid #3498db',
            borderRadius: '0.25rem',
            padding: '1rem',
            marginBottom: '2rem'
          }}>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#2c3e50' }}>
              <strong>Note:</strong> The assigned user will be notified and the ticket status will change to "In Progress".
            </p>
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedUser || users.length === 0}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: loading || !selectedUser || users.length === 0 ? '#95a5a6' : '#f39c12',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: loading || !selectedUser || users.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <FaUserCheck />
              {loading ? 'Assigning...' : 'Assign Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignTicketModal;
